/**
 * @module app.detour.Service
 */
import appBase from '../index.js';
import {styleFactory as detourStyleFactory} from '../detour/style.js';
import olFormatGeoJSON from 'ol/format/GeoJSON.js';
import olLayerVector from 'ol/layer/Vector.js';
import olSourceVector from 'ol/source/Vector.js';
import {MAP_PROJECTION, Z_INDEX} from "../constants";
import {objectForEach} from "../utils";

export default class DetourService {
  /**
   * @param {string} apiUrl
   * @param {app.MapService} appMapService
   * @ngInject
   */
  constructor(apiUrl, appMapService) {
    /**
     * @type {string}
     * @private
     */
    this.detourDataUrl_ = `${apiUrl}4/goodtoknow/detours`;
    /**
     * @type {app.MapService}
     * @private
     */
    this.appMapService_ = appMapService;

    /**
     * @type {ol.format.GeoJSON}
     * @private
     */
    this.format_ = new olFormatGeoJSON({
      dataProjection: MAP_PROJECTION
    });

    /**
     * @type {!number|null}
     * @private
     */
    this.highlightedId_ = null;

    /**
     * @type {boolean}
     * @private
     */
    this.protectedHighlight_ = false;

    /**
     * List of layernames ordered as the layertree.
     * @const
     * @type {!Array<string>}
     */
    this.LAYERS = [
      'Wanderland',
      'Veloland',
      'Mtbland',
      'Skatingland',
      'Kanuland',
      'Winterhiking',
      'Snowshoe',
      'Crosscountry',
      'Sledging'
    ].reverse();

    /**
     * Mapping from the layername to the land parameter of the detour view.
     * @const
     * @type {Object<string, string>}
     */
    this.LAYER_TO_LAND = {
      'Veloland': 'velo',
      'Wanderland': 'wander',
      'Mtbland': 'mountainbike',
      'Skatingland': 'skating',
      'Kanuland': 'kanu',
      'Winterhiking': 'winterhiking',
      'Snowshoe': 'snowshoe',
      'Crosscountry': 'crosscountry',
      'Sledging': 'sledging'
    };

    /**
     * @type {!Object<string, olLayerVector>}
     */
    this.detoursLayers_ = null;
  }

  /**
   * @return {!Promise<Object<string, !ol.layer.Vector>>}
   */
  getDetoursLayers() {
    if (!this.detoursLayers_) {
      // We must defer the creation of the layers
      // because the map is not yet set in the constructor.
      this.createDetoursLayers_();
    }
    return Promise.resolve(this.detoursLayers_);
  }

  /**
   * @private
   */
  createDetoursLayers_() {
    this.detoursLayers_ = {};
    for (const layername of this.LAYERS) {
      const land = this.LAYER_TO_LAND[layername];
      console.assert(land);
      const source = new olSourceVector({
        format: this.format_,
        url: `${this.detourDataUrl_}?land=${land}`
      });
      const layerPoints = new olLayerVector({
        'name': `detour_${land}_points`,
        zIndex: Z_INDEX.DETOURS_POINT,
        visible: false,
        style: detourStyleFactory(false, () => this.highlightedId_, 'Point'), // with maxResolution constraint
        'style3d': detourStyleFactory(true, () => this.highlightedId_, 'Point'), // without maxResolution constraint
        source: source,
        'altitudeMode': 'clampToGround',
        maxDistance: 20000 // for 3D
        // handle maxResolution in the style function to workaround OL-Cesium limitation
      });
      this.appMapService_.map.addLayer(layerPoints);
      this.detoursLayers_[`${layername}_points`] = layerPoints;
      const layerLines = new olLayerVector({
        'name': `detour_${land}_lines`,
        zIndex: Z_INDEX.DETOURS_LINE,
        visible: false,
        style: detourStyleFactory(false, () => this.highlightedId_, 'LineString'), // with maxResolution constraint
        'style3d': detourStyleFactory(true, () => this.highlightedId_, 'LineString'), // without maxResolution constraint
        source: source,
        opacity: 0.8,
        'altitudeMode': 'clampToGround',
        // handle maxResolution in the style function to workaround OL-Cesium limitation
      });
      this.appMapService_.map.addLayer(layerLines);
      this.detoursLayers_[`${layername}_lines`] = layerLines;
    }
  }

  /**
  * force the feature to update its styling
  */
  updateFeatures_(features) {
    objectForEach(features, feature => {
      feature.changed();
    });
  }

  /**
  * set highlighted feature as protected
  */
  toggleProtectedHighlight(state) {
    this.protectedHighlight_ = state;
  }

  /**
  * @param {number} id
  */
  highlightFeatures(id) {
    this.highlightedId_ = id;
    this.getHighlitedFeatures().then(highlightedFeatures => this.updateFeatures_(highlightedFeatures));
  }

  /**
  * remove highlight property on all features
  */
  unhighlightFeatures() {
    if (!this.protectedHighlight_) {
      // recovering the features must be done before reseting the highlight id
      this.getHighlitedFeatures().then(highlitedFeatures => {
        this.highlightedId_ = null;
        this.updateFeatures_(highlitedFeatures);
      });
    }
  }

  /**
  * get highlighted features
  * @return {Array.<ol.Feature>}
  */
  getHighlitedFeatures() {
    const highlitedFeatures = [];
    return this.getDetoursLayers().then(detours => {
      objectForEach(detours, detourlayer => {
        if (detourlayer.getVisible()) {
          // get all features from id
          detourlayer.getSource().forEachFeature(feature => {
            if (feature.get('id') == this.highlightedId_) {
              highlitedFeatures.push(feature);
            }
          });
        }
      });
      return highlitedFeatures;
    });
  }

  /**
   * get visible features
   * @return {Array.<ol.Feature>}
   */
  getVisibleFeatures() {
    const features = [];
    return this.getDetoursLayers().then(detours => {
      objectForEach(detours, detourlayer => {
        if (detourlayer.getVisible()) {
          // get all features from id
          detourlayer.getSource().forEachFeature(feature => {
            features.push(feature);
          });
        }
      });
      return features;
    });
  }
}

appBase.service('appDetourService', DetourService);
